// client/src/components/Reservations/components/ReservationCard.jsx
import React from 'react';
import { DateTime } from 'luxon';
import { formatMoney } from '../utils/money';

const STATUS_CONFIG = {
  active: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
    )
  },
  confirmed: {
    bg: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
    )
  },
  pending: {
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 001.415-1.415L11 9.586V6z" clipRule="evenodd" />
      </svg>
    )
  },
  cancelled: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    icon: (
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    )
  }
};

// Item-level status config; display label maps 'confirmed' -> 'active'
const ITEM_STATUS_CONFIG = {
  active:    { label: 'active', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  confirmed: { label: 'active', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  pending:   { label: 'pending', bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  cancelled: { label: 'cancelled', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' }
};

export default function ReservationCard({ reservation, onClick, onViewDetails, onArchive }) {
  // Debug: log reservation prop to inspect items/offering_name
  try {
    console.log('[debug] ReservationCard reservation prop', reservation);
    console.log('[debug] ReservationCard items', reservation?.items);
  } catch (e) {
    console.warn('[debug] failed to log reservation prop', e);
  }
  // Try multiple shapes to derive a valid DateTime
  const deriveDateTime = () => {
    const directIso = reservation.start_at || reservation.start_iso || reservation.slot_start;
    if (directIso) {
      // Parse reservation ISO in UTC to avoid local timezone shifts when showing the booked hour
      const dt = DateTime.fromISO(String(directIso), { zone: 'utc' });
      if (dt.isValid) return dt;
    }
    const d = reservation.date;
    const t = reservation.time || reservation.start_time || reservation.start_hhmm;
    if (d && t) {
      const dt = DateTime.fromISO(`${d}T${t}`);
      if (dt.isValid) return dt;
    }
    // Try from first item if available
    const it = Array.isArray(reservation.items) ? reservation.items[0] : null;
    const itIso = it?.start_at || it?.slot_start || it?.start_iso;
    if (itIso) {
      // Parse item ISO in UTC as well
      const dt = DateTime.fromISO(String(itIso), { zone: 'utc' });
      if (dt.isValid) return dt;
    }
    // Bundle: use bundle_start_date if provided
    const bStart = it?.guests_json?.bundle_start_date;
    if (bStart) {
      const dt = DateTime.fromISO(`${bStart}T00:00:00`, { zone: 'utc' });
      if (dt.isValid) return dt;
    }
    return DateTime.invalid('Invalid Reservation Date');
  };

  const reservationDateTime = deriveDateTime();
  const now = DateTime.utc();
  const isValidDt = reservationDateTime?.isValid;
  const isPast = isValidDt ? reservationDateTime <= now : false;
  const isToday = isValidDt ? reservationDateTime.hasSame(now, 'day') : false;
  const isTomorrow = isValidDt ? reservationDateTime.hasSame(now.plus({ days: 1 }), 'day') : false;

  const formatDate = () => {
    if (!isValidDt) return 'Date TBD';
    if (isToday) return 'Today';
    if (isTomorrow) return 'Tomorrow';
    return reservationDateTime.toFormat('EEE, MMM dd, yyyy');
  };

  const formatTime = () => {
  if (!isValidDt) return '—';
  // Format using UTC so displayed hour matches stored UTC timestamp
  return reservationDateTime.setZone('utc').toFormat('h:mm a');
  };

  const firstItemStatus = String(reservation.items?.[0]?.status || '').toLowerCase();
  const headerStatusRaw = firstItemStatus || String(reservation.status || 'pending').toLowerCase();
  // Normalize header status: treat 'confirmed' as 'active'
  const headerStatus = headerStatusRaw === 'confirmed' ? 'active' : headerStatusRaw;
  const statusConfig = STATUS_CONFIG[headerStatus] || STATUS_CONFIG.pending;
  const headerLabel = headerStatus;
  
  // Calculate group size from items
  const totalPeople = reservation.items?.reduce((total, item) => {
    return total + (Number(item.party_size || item.guests || 1));
  }, 0) || reservation.party_size || 1;

  const isBundle = Array.isArray(reservation.items) && reservation.items.some(i => i?.offering_kind === 'bundle');

  // Map backend rc_mode -> reservation item offering_kind + display label
  const modeToKind = (mode) => {
    const m = String(mode || '').toLowerCase();
    switch (m) {
      case 'personal_services': return 'ps_service';
      case 'class_based': return 'cb_class';
      case 'modular_seating': return 'ms_table_type';
      case 'accommodation': return 'ac_room_type';
      default: return null;
    }
  };
  const modeLabel = (mode) => {
    const m = String(mode || '').toLowerCase();
    switch (m) {
      case 'personal_services': return 'Personal service';
      case 'class_based': return 'Class';
      case 'modular_seating': return 'Table';
      case 'accommodation': return 'Room';
      default: return 'Service';
    }
  };

  const fmtItemTime = (item) => {
    const iso = item?.slot_start || item?.start_at || item?.start_iso;
    const t = item?.time || item?.start_time || item?.start_hhmm;
    if (iso) {
      const dt = DateTime.fromISO(String(iso), { zone: 'utc' });
      return dt.isValid ? dt.toFormat('h:mm a') : '—';
    }
    if (t) {
      try {
        const baseDate = reservationDateTime?.isValid ? reservationDateTime.toISODate() : (reservation.date || '');
        const dt = baseDate ? DateTime.fromISO(`${baseDate}T${t}`) : DateTime.invalid('no-base');
        return dt.isValid ? dt.toFormat('h:mm a') : t;
      } catch { return t; }
    }
    return reservationDateTime?.isValid ? reservationDateTime.setZone('utc').toFormat('h:mm a') : '—';
  };
  const fmtItemGuests = (item) => Number(item?.party_size || item?.guests || 1);
  const fmtAcStartDate = (item) => {
    const d = item?.ac_nights_start || item?.nights_start;
    if (!d) return '—';
    const dt = DateTime.fromISO(String(d), { zone: 'utc' });
    return dt.isValid ? dt.toFormat('MMM dd') : '—';
  };
  const fmtAcNights = (item) => {
    const n = item?.ac_nights_count ?? item?.nights_count;
    if (n == null) return '—';
    const num = Number(n);
    return `${num} ${num === 1 ? 'night' : 'nights'}`;
  };

  // Per-item helper: name, guests, time
  const itemRows = (reservation.items || []).map((item) => {
    // Endpoint builds 'offering_name' from ri.meta->>'offering_name'
    const name = item.offering_name || item.guests_json?.offering_name || item.service_name || item.name || 'Service';
    const guests = Number(item.party_size || item.guests || 1);
    // Prefer item-specific time if present
    const itemIso = item.slot_start || item.start_at || item.start_iso;
    const itemTimeStr = item.time || item.start_time || item.start_hhmm;
    let timeLabel = '';
    // Price: endpoint includes 'final_price' field
    const rawPrice = Number(item.final_price ?? item.final_price_amount ?? item.price ?? 0);
    const status = String(item.status || '').toLowerCase();
    // For accommodation items, show Start date + Nights instead of a time
    if (item.offering_kind === 'ac_room_type') {
      return { name, guests, timeLabel: `${fmtAcStartDate(item)} · ${fmtAcNights(item)}`, rawPrice, status, kind: item.offering_kind, bundleChildren: item.bundle_children, bundleWindow: item.bundle_window_days, bundleStart: item.guests_json?.bundle_start_date };
    }
    if (itemIso) {
      // Parse item ISO in UTC and format in UTC
      const dt = DateTime.fromISO(String(itemIso), { zone: 'utc' });
      timeLabel = dt.isValid ? dt.toFormat('h:mm a') : '';
    } else if (itemTimeStr) {
      try {
        const isoStr = `${reservationDateTime.toISODate()}T${itemTimeStr}`;
        const dt = isValidDt ? DateTime.fromISO(isoStr, { zone: 'utc' }) : DateTime.invalid('no-date');
        timeLabel = dt.isValid ? dt.toFormat('h:mm a') : itemTimeStr;
      } catch {
        timeLabel = itemTimeStr;
      }
    } else {
      timeLabel = isValidDt ? reservationDateTime.setZone('utc').toFormat('h:mm a') : '—';
    }

    return { name, guests, timeLabel, rawPrice, status, kind: item.offering_kind, bundleChildren: item.bundle_children, bundleWindow: item.bundle_window_days, bundleStart: item.guests_json?.bundle_start_date };
  });
console.log(itemRows);
  // Determine if the single shown item (cards receive reservation with items:[it]) is cancelled
  const firstItem = Array.isArray(reservation.items) ? reservation.items[0] : null;
  const itemIsCancelled = String(firstItem?.status || '').toLowerCase() === 'cancelled';

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden ${
        isPast ? 'opacity-75' : ''
      }`}
    >
      <div className="p-6">
        <div className="flex items-start justify-between">
          {/* Left Section - Main Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-3 mb-3">
              {/* Business/Service Info */}
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {reservation.business_name || 'Unknown Business'}
                </h3>
                <div className="text-sm text-gray-500 mt-0.5">{formatDate()} · {formatTime()}</div>
                {/* Removed single primary service; detailed list shown below */}
              </div>

              {/* Status Badge */}
              <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border`}>
                {statusConfig.icon}
                <span className="ml-1 capitalize">{headerLabel}</span>
              </div>
            </div>

            {/* Date/Time shown under business name; per-item price shown in the item row below. */}

            {/* Per-service breakdown */}
            {itemRows.length > 0 && (
              <div className="mt-4">
                <div className="text-sm text-gray-700 font-medium mb-1">{isBundle ? 'Bundle' : 'Services'}</div>
                <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                  {itemRows.map((row, idx) => {
                    const isBundleRow = row.kind === 'bundle';
                    if (isBundleRow) {
                      // Render bundle header row with consumption window summary
                      const windowLabel = row.bundleStart && row.bundleWindow
                        ? (() => {
                            try {
                              const start = DateTime.fromISO(String(row.bundleStart));
                              if (!start.isValid) return `Window: ${row.bundleWindow} day(s)`;
                              const end = start.plus({ days: Math.max(1, Number(row.bundleWindow)) - 1 });
                              return `Window: ${start.toFormat('MMM d')} – ${end.toFormat('MMM d')}`;
                            } catch {
                              return `Window: ${row.bundleWindow} day(s)`;
                            }
                          })()
                        : null;
                      return (
                        <li key={`bundle-${idx}`} className="px-3 py-2 text-sm">
                          <div className="flex items-center justify-between">
                            <div className="truncate">
                              <span className="text-gray-900 font-semibold">{row.name}</span>
                              {windowLabel && (
                                <span className="ml-2 text-gray-500">{windowLabel}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-4">
                              {Number(row.rawPrice) > 0 && (
                                <span className="text-gray-900 font-semibold whitespace-nowrap">{formatMoney(Number(row.rawPrice), reservation.currency)}</span>
                              )}
                              <button onClick={onClick} className="text-blue-600 hover:text-blue-800 whitespace-nowrap">View details</button>
                            </div>
                          </div>
                          {/* Included services */}
                          {Array.isArray(row.bundleChildren) && row.bundleChildren.length > 0 && (
                            <ul className="mt-2 pl-3 border-l border-gray-200 space-y-1">
                              {row.bundleChildren.map((c, j) => {
                                const kind = c.offering_kind || modeToKind(c.mode);
                                // Prefer pre-joined fields from backend; fallback to a local match
                                const fromServer = c.slot_start || c.party_size != null;
                                const match = fromServer ? null : (reservation.items || []).find(x => x && x.offering_kind !== 'bundle' && x.offering_kind === kind && String(x.offering_id) === String(c.item_id));
                                const isAc = (kind === 'ac_room_type');
                                const timeStr = isAc
                                  ? `${fmtAcStartDate(c)} · ${fmtAcNights(c)}`
                                  : c.slot_start
                                  ? (() => { const dt = DateTime.fromISO(String(c.slot_start), { zone:'utc' }); return dt.isValid ? dt.toFormat('h:mm a') : '—'; })()
                                  : (match ? fmtItemTime(match) : '—');
                                const guests = c.party_size != null ? Number(c.party_size) : (match ? fmtItemGuests(match) : null);
                                return (
                                  <li key={`child-${idx}-${j}`} className="flex items-center justify-between gap-3">
                                    <div className="min-w-0 flex items-center gap-2">
                                      <span className="inline-block text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200 whitespace-nowrap">{modeLabel(c.mode)}</span>
                                      <span className="text-gray-700 truncate">{c.name || 'Included service'}</span>
                                    </div>
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                      <span className="text-gray-600 whitespace-nowrap">{timeStr}</span>
                                      {guests != null && (
                                        <span className="text-gray-900 font-medium whitespace-nowrap">{guests} {guests === 1 ? 'guest' : 'guests'}</span>
                                      )}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onViewDetails?.(reservation, {
                                            itemId: match?.id || null,
                                            offeringKind: kind,
                                            offeringId: c.item_id,
                                            childMeta: c,
                                          });
                                        }}
                                        className="text-blue-600 hover:text-blue-800 text-sm"
                                      >
                                        View details
                                      </button>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </li>
                      );
                    }
                    // Default (standalone service) row
                    return (
                      <li key={idx} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div className="truncate">
                          <span className="text-gray-900 font-medium">{row.name}</span>
                          {row.status && (
                            (() => {
                              const cfg = ITEM_STATUS_CONFIG[row.status] || ITEM_STATUS_CONFIG.pending;
                              return (
                                <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cfg.bg} ${cfg.text} ${cfg.border} border`}>
                                  {cfg.label}
                                </span>
                              );
                            })()
                          )}
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <span className="text-gray-600 whitespace-nowrap">{row.timeLabel}</span>
                          <span className="text-gray-900 font-medium whitespace-nowrap">{row.guests} {row.guests === 1 ? 'guest' : 'guests'}</span>
                          {Number(row.rawPrice) > 0 && (
                            <span className="text-gray-900 font-semibold whitespace-nowrap">{formatMoney(Number(row.rawPrice), reservation.currency)}</span>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const it = (reservation.items || [])[0];
                              onViewDetails?.(reservation, it ? { itemId: it.id, offeringKind: it.offering_kind, offeringId: it.offering_id } : undefined);
                            }}
                            className="text-blue-600 hover:text-blue-800 whitespace-nowrap"
                          >
                            View details
                          </button>
                          {(itemIsCancelled || isPast) && firstItem?.id && (
                            <button
                              title="Remove from my list"
                              onClick={(e) => {
                                e.stopPropagation();
                                onArchive?.({ reservation, itemId: firstItem.id });
                              }}
                              className="text-gray-400 hover:text-red-600"
                              aria-label="Remove reservation item"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                                <path fillRule="evenodd" d="M9 3.75A2.25 2.25 0 0 1 11.25 1.5h1.5A2.25 2.25 0 0 1 15 3.75V4.5h3.75a.75.75 0 0 1 0 1.5h-.525l-1.05 12.6A3 3 0 0 1 14.19 21.5H9.81a3 3 0 0 1-2.985-2.9L5.775 6H5.25a.75.75 0 0 1 0-1.5H9V3.75Zm1.5.75h3V3.75a.75.75 0 0 0-.75-.75h-1.5a.75.75 0 0 0-.75.75V4.5ZM7.275 6l1.05 12.6a1.5 1.5 0 0 0 1.485 1.4h4.38a1.5 1.5 0 0 0 1.485-1.4L16.725 6H7.275ZM9.75 9a.75.75 0 0 1 .75.75v7.5a.75.75 0 0 1-1.5 0v-7.5A.75.75 0 0 1 9.75 9Zm4.5 0a.75.75 0 0 1 .75.75v7.5a.75.75 0 0 1-1.5 0v-7.5a.75.75 0 0 1 .75-.75Z" clipRule="evenodd" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          {/* Right Section - Action Indicator */}
          <div className="flex-shrink-0 ml-4">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>

        {/* Special Notes */}
        {reservation.special_requests && (
          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-start">
              <svg className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-blue-800 font-medium">Special Request</p>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions Bar (only for upcoming reservations) */}
      {isValidDt && !isPast && headerStatus !== 'cancelled' && (
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600">{isToday ? 'Today' : `${Math.max(0, Math.ceil(reservationDateTime.diffNow('days').days))} days to go`}</span>
            <div className="flex space-x-4">
              <button className="text-blue-600 hover:text-blue-800 font-medium">
                View Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}